import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config/api';
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Collapse,
  Toolbar,
  CircularProgress,
  Chip,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  Psychology as AIIcon,
  ExpandLess,
  ExpandMore,
  School as SubjectIcon,
  MenuBook as CourseIcon,
} from '@mui/icons-material';

// Domain colors matching the current cleaned CHISG set
const DOMAIN_COLORS: Record<string, string> = {
  'Foundational': '#e65100',
  'Focus & Tools': '#1565c0',
  'Language': '#00838f',
  'Numbers': '#4527a0',
  'People': '#2e7d32',
  'Physical': '#558b2f',
  'Problem Solving': '#6a1b9a',
  'Self': '#c62828',
};

interface Subject {
  subject: string;
  course_count: number;
}

interface Course {
  course: string;
  courseCode: string;
  subject: string;
  keyStage: string;
  yearGroup: string;
  description: string;
  coreSkills: string[];
  practiceSkills: string[];
  implicitSkills: string[];
}

interface NavigationDrawerProps {
  open: boolean;
  onClose: () => void;
  width: number;
  variant: 'permanent' | 'persistent' | 'temporary';
  selectedDomain: string | null;
  selectedSubject: string | null;
  selectedCourse: string | null;
  onDomainSelect: (domain: string) => void;
  onSubjectSelect: (subject: string) => void;
  onCourseSelect: (courseName: string, coreSkills: string[]) => void;
  onSearchClick: () => void;
  onAIAssistClick: () => void;
}

const NavigationDrawer: React.FC<NavigationDrawerProps> = ({
  open,
  onClose,
  width,
  variant,
  selectedDomain,
  selectedSubject,
  selectedCourse,
  onDomainSelect,
  onSubjectSelect,
  onCourseSelect,
  onSearchClick,
  onAIAssistClick,
}) => {
  const [domains] = useState<string[]>(Object.keys(DOMAIN_COLORS));
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedDomain, setExpandedDomain] = useState<string | null>(null);
  const [expandedSubject, setExpandedSubject] = useState<string | null>(null);

  // Fetch subjects on mount
  useEffect(() => {
    fetchSubjects();
  }, []);

  // Fetch courses when subject selected
  useEffect(() => {
    if (selectedSubject) {
      fetchCoursesBySubject(selectedSubject);
    }
  }, [selectedSubject]);

  const fetchSubjects = async () => {
    try {
      setLoading(true);
      // Using the course suggestions API to get subjects
      const response = await fetch(`${API_BASE_URL}/api/courses/subjects`);
      if (response.ok) {
        const data = await response.json();
        setSubjects(data.subjects || []);
      }
    } catch (error) {
      console.error('Failed to fetch subjects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCoursesBySubject = async (subject: string) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/courses/by-subject/${encodeURIComponent(subject)}`);
      if (response.ok) {
        const data = await response.json();
        // API returns courses in data.data, not data.courses
        setCourses(data.data || data.courses || []);
      }
    } catch (error) {
      console.error('Failed to fetch courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDomainClick = (domain: string) => {
    if (expandedDomain === domain) {
      setExpandedDomain(null);
    } else {
      setExpandedDomain(domain);
      onDomainSelect(domain);
    }
  };

  const handleSubjectClick = (subject: string) => {
    if (expandedSubject === subject) {
      setExpandedSubject(null);
    } else {
      setExpandedSubject(subject);
      onSubjectSelect(subject);
    }
  };

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      sx={{
        width: open ? width : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: width,
          boxSizing: 'border-box',
          backgroundColor: '#2D2D3A',
          color: '#fff',
        },
      }}
    >
      <Toolbar /> {/* Spacer for AppBar */}
      
      <Box sx={{ overflow: 'auto', height: '100%' }}>
        {/* Action Buttons */}
        <Box sx={{ p: 2 }}>
          <Stack direction="row" spacing={1}>
            <Chip
              icon={<SearchIcon />}
              label="Search"
              onClick={onSearchClick}
              sx={{
                backgroundColor: '#3D3D4A',
                color: '#fff',
                '&:hover': { backgroundColor: '#4D4D5A' },
                flex: 1,
              }}
            />
            <Chip
              icon={<AIIcon />}
              label="AI Assist"
              onClick={onAIAssistClick}
              sx={{
                backgroundColor: '#009688',
                color: '#fff',
                '&:hover': { backgroundColor: '#00796B' },
                flex: 1,
              }}
            />
          </Stack>
        </Box>

        <Divider sx={{ borderColor: '#444' }} />

        {/* Domains Section */}
        <Box sx={{ p: 1 }}>
          <Typography variant="subtitle2" sx={{ px: 1, py: 0.5, color: '#888' }}>
            DOMAINS
          </Typography>
          <List dense>
            {domains.map((domain) => (
              <ListItem key={domain} disablePadding>
                <ListItemButton
                  selected={selectedDomain === domain}
                  onClick={() => handleDomainClick(domain)}
                  sx={{
                    borderRadius: 1,
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(0, 150, 136, 0.2)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: DOMAIN_COLORS[domain] || '#666',
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary={domain} 
                    primaryTypographyProps={{ 
                      variant: 'body2',
                      sx: { fontSize: '0.8rem' }
                    }} 
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>

        <Divider sx={{ borderColor: '#444' }} />

        {/* Subjects & Courses Section */}
        <Box sx={{ p: 1 }}>
          <Typography variant="subtitle2" sx={{ px: 1, py: 0.5, color: '#888' }}>
            SUBJECTS & COURSES
          </Typography>
          
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} sx={{ color: '#009688' }} />
            </Box>
          )}

          <List dense>
            {subjects.map((subject) => (
              <React.Fragment key={subject.subject}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleSubjectClick(subject.subject)}
                    sx={{ borderRadius: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <SubjectIcon sx={{ fontSize: 18, color: '#888' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={subject.subject}
                      secondary={`${subject.course_count} courses`}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ 
                        variant: 'caption',
                        sx: { color: '#666' }
                      }}
                    />
                    {expandedSubject === subject.subject ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>
                
                <Collapse in={expandedSubject === subject.subject} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding dense>
                    {courses
                      .filter(c => c.subject === subject.subject)
                      .map((course) => (
                        <ListItem key={course.course} disablePadding>
                          <ListItemButton
                            selected={selectedCourse === course.course}
                            onClick={() => onCourseSelect(course.course, course.coreSkills || [])}
                            sx={{
                              pl: 4,
                              borderRadius: 1,
                              '&.Mui-selected': {
                                backgroundColor: 'rgba(0, 150, 136, 0.2)',
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <CourseIcon sx={{ fontSize: 16, color: '#009688' }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={course.course}
                              secondary={`${(course.coreSkills || []).length} skills`}
                              primaryTypographyProps={{ 
                                variant: 'body2',
                                sx: { fontSize: '0.75rem' }
                              }}
                              secondaryTypographyProps={{ 
                                variant: 'caption',
                                sx: { color: '#666', fontSize: '0.65rem' }
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                  </List>
                </Collapse>
              </React.Fragment>
            ))}
          </List>
          <Box sx={{ p: 2, mt: 2, borderTop: '1px solid rgba(255,255,255,0.1)', textAlign: 'center' }}>
             <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
               Current skills map (591 skills / 1648 links)
             </Typography>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default NavigationDrawer;
